import NetInfo from "@react-native-community/netinfo";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  submitOpenHouseAttendee,
  syncOpenHouseAttendees,
} from "../services/attendeeSubmission";

const RETRY_INTERVAL_MS = 10_000;

export const useOpenHouseAttendeeSubmission = (
  openHouseUuid?: string,
  primaryAgentUserId?: string | number
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef<Promise<any> | null>(null);
  const isSyncingRef = useRef(false);

  const syncAttendees = useCallback(async () => {
    if (!openHouseUuid || !primaryAgentUserId || isSyncingRef.current) return;
    isSyncingRef.current = true;
    try {
      await syncOpenHouseAttendees(openHouseUuid, primaryAgentUserId);
    } catch (error) {
      console.error("Attendee sync remains queued:", error);
    } finally {
      isSyncingRef.current = false;
    }
  }, [openHouseUuid, primaryAgentUserId]);

  useEffect(() => {
    void syncAttendees();
    const interval = setInterval(() => void syncAttendees(), RETRY_INTERVAL_MS);
    const unsubscribe = NetInfo.addEventListener((state) => {
      if (state.isConnected && state.isInternetReachable !== false) {
        void syncAttendees();
      }
    });
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [syncAttendees]);

  const submitAttendee = useCallback(
    (attendee: any) => {
      if (submitInFlightRef.current) return submitInFlightRef.current;
      if (!openHouseUuid || !primaryAgentUserId) {
        return Promise.reject(
          new Error("Open house and primary agent are required.")
        );
      }

      const operation = (async () => {
        setIsSubmitting(true);
        return submitOpenHouseAttendee(
          openHouseUuid,
          primaryAgentUserId,
          attendee
        );
      })();

      submitInFlightRef.current = operation;
      operation.then(
        () => {
          submitInFlightRef.current = null;
          setIsSubmitting(false);
        },
        () => {
          submitInFlightRef.current = null;
          setIsSubmitting(false);
        }
      );
      return operation;
    },
    [openHouseUuid, primaryAgentUserId]
  );

  return { submitAttendee, syncAttendees, isSubmitting };
};

export default useOpenHouseAttendeeSubmission;
